/**
 * The two-player Sim 9 match scorecard (screens/sim9/Sim9MatchBoard.tsx) as
 * data: per hole, the distance, par, and each player's strokes with golf
 * notation (circle = birdie, double circle = eagle or better, square = bogey,
 * double square = double bogey or worse) and the won-hole highlight. Pure;
 * shared by the phone board and both TV receivers. Rules moved out of
 * Sim9MatchBoard unchanged.
 */

export type ScoreMarkShape = 'circle' | 'square';

export interface ScoreMark {
  shape: ScoreMarkShape | null;
  doubled: boolean;
}

/** Golf notation for a score against par. */
export function scoreMark(strokes: number, par: number): ScoreMark {
  const diff = strokes - par;
  const shape: ScoreMarkShape | null = diff <= -1 ? 'circle' : diff >= 1 ? 'square' : null;
  return { shape, doubled: diff <= -2 || diff >= 2 };
}

/** 0 or 1 for the player who won the hole, null for a halve or an unplayed hole. */
export function holeWinner(a: number | null, b: number | null): 0 | 1 | null {
  return a == null || b == null ? null : a === b ? null : a < b ? 0 : 1;
}

export interface Sim9MatchBoardCell {
  strokes: number | null;
  mark: ScoreMark | null;
  won: boolean;
}

export interface Sim9MatchBoardHole {
  number: number;
  distanceFt: number;
  par: number;
  cells: [Sim9MatchBoardCell, Sim9MatchBoardCell];
}

export function sim9MatchBoardModel(
  holes: readonly number[],
  pars: readonly number[],
  holeStrokes: ReadonlyArray<readonly [number | null, number | null]>,
): Sim9MatchBoardHole[] {
  return holes.map((distanceFt, i) => {
    const [a, b] = holeStrokes[i] ?? [null, null];
    const par = pars[i];
    const winner = holeWinner(a, b);
    const cell = (strokes: number | null, index: 0 | 1): Sim9MatchBoardCell => ({
      strokes,
      mark: strokes == null ? null : scoreMark(strokes, par),
      won: winner === index,
    });
    return { number: i + 1, distanceFt, par, cells: [cell(a, 0), cell(b, 1)] };
  });
}

export interface Sim9ScoreBoardHole {
  number: number;
  distanceFt: number;
  par: number;
  /** One cell per player, in row order. */
  cells: Sim9MatchBoardCell[];
}

/**
 * The same scorecard for any number of players (a Sim 9 stroke-play round of
 * up to six, 2026-09-30). The won-hole highlight is a two-player idea, so it
 * shows only with exactly two rows; one row or three and more draw the
 * notation alone.
 */
export function sim9ScoreBoardModel(
  holes: readonly number[],
  pars: readonly number[],
  players: ReadonlyArray<ReadonlyArray<number | null>>,
): Sim9ScoreBoardHole[] {
  return holes.map((distanceFt, i) => {
    const par = pars[i];
    const strokes = players.map((row) => row[i] ?? null);
    const winner = players.length === 2 ? holeWinner(strokes[0], strokes[1]) : null;
    return {
      number: i + 1,
      distanceFt,
      par,
      cells: strokes.map((value, index) => ({
        strokes: value,
        mark: value == null ? null : scoreMark(value, par),
        won: winner === index,
      })),
    };
  });
}

/** The round's totals for the board's TOT column (Cole, 2026-10-02: "should
 *  show a total round score, even though it's match play"): total distance,
 *  par, and each player's strokes over the holes played (null before any). */
export function sim9ScoreBoardTotals(holes: readonly Sim9ScoreBoardHole[]): {
  distanceFt: number;
  par: number;
  strokes: Array<number | null>;
} {
  const players = holes[0]?.cells.length ?? 0;
  const strokes: Array<number | null> = Array.from({ length: players }, () => null);
  for (const hole of holes) {
    hole.cells.forEach((cell, index) => {
      if (cell.strokes == null) return;
      strokes[index] = (strokes[index] ?? 0) + cell.strokes;
    });
  }
  return {
    distanceFt: holes.reduce((sum, hole) => sum + hole.distanceFt, 0),
    par: holes.reduce((sum, hole) => sum + hole.par, 0),
    strokes,
  };
}
